<form (ngSubmit)="submit()" [formGroup]="form" class="flex flex-col gap-6">
  <h3>Editar empleado</h3>

  <input (change)="handleAvatarSelected($event)" accept="image/*" class="hidden" id="edit-employee-avatar-input" type="file">
  <button (click)="openAvatarPicker()" [disabled]="isSubmitting" class="btn btn-neutral-outline avatar !w-[3rem] !h-[3rem] overflow-hidden" type="button">
    @if (displayedAvatarUrl) {
      <img [src]="displayedAvatarUrl" alt="Foto de perfil" class="h-full w-full object-cover">
    }
  </button>

  <div class="w-full flex flex-col lg:flex-row gap-8 items-start">
    <div class="w-full lg:flex-[1.2]">
      <core-employee-form-sections
        [contractFileName]="contractFileName"
        [form]="form"
        [groupOptions]="groupOptions"
        [isLoadingOptions]="isLoadingOptions"
        [isSubmitting]="isSubmitting"
        [pendingAttachments]="pendingAttachments"
        [roleDisabled]="false"
        [roleOptions]="roleOptions"
        [showRoleField]="!isOwnerEditingSelf"
        [showDocumentationSection]="false"
        (contractSelected)="handleContractSelected($event)"/>
    </div>

    <div class="w-full lg:flex-1 rounded-2xl bg-neutral-100 p-4">
      <input (change)="handleDocumentsInput($event)" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" class="hidden" id="edit-employee-documents-input" multiple type="file">

      <core-tabs [config]="[
        { slug: 'docs', label: 'Documentación adjunta', template: docsTabTpl},
        { slug: 'groups', label: 'Grupos asociados', template: groupsTabTpl},
        { slug: 'influences', label: 'Influencia', template: influencesTabTpl}
      ]"/>
    </div>
  </div>

  @if (errorMessage) {
    <span class="cd-text-b4 text-error-600">{{ errorMessage }}</span>
  }

  <div class="w-full flex flex-row items-center gap-4">
    <button [disabled]="isSubmitting || isLoadingOptions" class="btn btn-primary flex flex-row gap-2 items-center min-w-[3rem]" type="submit"
            style="border-radius: 0.5rem;padding: 0.5rem 1rem">
      <img alt="Icon" class="filter-neutral-100 h-4" src="/assets/icons/feather/save.svg">
      <span class="hidden lg:block cd-text-md truncate capitalize">
        {{ isSubmitting ? 'Guardando...' : 'Guardar cambios' }}
      </span>
    </button>

    <button (click)="close()" [disabled]="isSubmitting" class="btn btn-primary-soft flex flex-row gap-2 items-center min-w-[3rem]" type="button"
            style="border-radius: 0.5rem;padding: 0.5rem 1rem">
      <img alt="Icon" class="filter-primary-600 h-4" src="/assets/icons/feather/x.svg">
      <span class="hidden lg:block cd-text-md truncate capitalize">Cancelar</span>
    </button>

    <button (click)="confirmDeactivate()" [disabled]="isSubmitting" class="btn btn-primary-soft flex flex-row gap-2 items-center min-w-[3rem]" type="button"
            style="border-radius: 0.5rem;padding: 0.5rem 1rem">
      <img alt="Icon" class="filter-primary-600 h-4" src="/assets/icons/feather/arrow-down-circle.svg">
      <span class="hidden lg:block cd-text-md truncate capitalize">Dar de baja</span>
    </button>
  </div>
</form>

<ng-template #docsTabTpl let-slug>
  <div class="flex flex-col gap-3">
    <div class="flex flex-row justify-between items-center border-b-1 pb-2 gap-3">
      <span class="cd-text-b3 text-neutral-700">Documentación adjunta</span>
      <button (click)="openDocumentsPicker()" [disabled]="isSubmitting" class="btn btn-neutral-text !px-1 !py-1" type="button">
        <img alt="Adjuntar documentos" class="filter-neutral-800 h-4" src="/assets/icons/feather/paperclip.svg">
      </button>
    </div>

    @if (isLoadingDetail) {
      @for (item of [1, 2, 3]; track item) {
        <div class="flex items-start gap-6 rounded-lg bg-neutral px-3 py-3">
          <div class="h-8 w-8 skeleton skeleton-rect rounded-md shrink-0"></div>
          <div class="flex flex-col gap-2 flex-1 min-w-0">
            <div class="h-4 w-28 skeleton skeleton-rect rounded-full"></div>
            <div class="h-4 w-3/4 skeleton skeleton-rect rounded-md"></div>
            <div class="h-3 w-24 skeleton skeleton-rect rounded-md"></div>
          </div>
          <div class="flex gap-2 shrink-0">
            <div class="h-8 w-8 skeleton skeleton-rect rounded-md"></div>
            <div class="h-8 w-8 skeleton skeleton-rect rounded-md"></div>
          </div>
        </div>
      }
    } @else {
      @for (file of pendingAttachments; track file.name + '-' + file.size + '-' + $index) {
        <core-file-widget
          [badge]="isContractAttachment(file.name) ? 'Contrato' : ''"
          [fileName]="file.name"
          [fileSize]="((file.size || 0) / 1024).toFixed(2) + ' KB'"
          [showDownload]="false"
          (remove)="removePendingAttachment($index)"/>
      }

      @for (file of existingAttachments; track file.id) {
        <core-file-widget
          [badge]="isContractAttachment(file.fileName) ? 'Contrato' : ''"
          [fileName]="file.fileName"
          [fileSize]="((file.fileSize || 0) / 1024).toFixed(2) + ' KB'"
          (download)="downloadAttachment(file)"
          (remove)="removeAttachment(file)"/>
      }

      @if (!pendingAttachments.length && !existingAttachments.length) {
        <span class="cd-text-b3 text-neutral-500">Sin documentos adjuntos.</span>
      }
    }
  </div>
</ng-template>

<ng-template #groupsTabTpl let-slug>
  <div class="flex flex-col gap-3">
    @for (group of selectedGroups; track group.id) {
      <div class="flex flex-row items-center gap-3 rounded-xl bg-white px-3 py-2 shadow-e1">
        <span class="cd-text-b3 font-medium">{{ group.name }}</span>
        @if (isSelectedGroupManager(group.id)) {
          <span class="ml-auto cd-text-b5 text-primary-700 bg-primary-100 px-2 py-1 rounded-full">Responsable</span>
        }
      </div>
    }

    @if (!selectedGroups.length) {
      <span class="cd-text-b3 text-neutral-500">Sin grupos asociados.</span>
    }
  </div>
</ng-template>

<ng-template #influencesTabTpl let-slug>
  <span class="cd-text-b3 text-neutral-500">Aquí verás esta información cuando el módulo esté disponible.</span>
</ng-template>
